import { ForwardedRef, forwardRef, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-enterprise'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-balham.css'
import { agGridOptions, defaultColDef, gridOptions as defaultGridOptions } from '@/components/table/grid_table_options'
import { cloneDeep, min } from 'lodash-es'
import { HStack } from '@musinsa/mwms-ds'
import Button from '@/components/content/Button'
import useModal from '@/hooks/useModal'
import FileInput from '@/components/content/FileInput'
import { GridTableBtnClickAction, GridTableProps } from '@/components/table/grid_types'
import RemixIcon from '@/components/content/RemixIcon'
import { CellValueChangedEvent, FilterChangedEvent, GetRowIdParams, ICellRendererParams, SelectionChangedEvent } from 'ag-grid-community'
import MToast from '@/utils/modal/MToast'
import MConfirm from '@/utils/modal/MConfirm'
import { Utils } from '@/utils/Utils'
import {
  AgGridReactWrap,
  GridTableBtnList,
  GridTableWrap,
  GridTitleWrap,
  GridToolBarSearch,
  GridToolBarWrap,
  GridToolPagination,
  TABLE_HEADER_EDITABLE,
} from '@/components/table/grid_styles'
import Text from '@/components/content/Text'
import { colors, colorSystem, borderRadiusSystem } from '@/styles/theme'
import ExcelDownloadModal from '@/components/table/ExcelDownloadModal'
import { useCheckedNum } from './GridTable.atoms'
import useClickOutside from '@/hooks/useClickOutside'
import styled from 'styled-components'
import { useTableColumnDefs } from '@/hooks/useTableColumnDefs'

function GridTableInner<T>(props: GridTableProps<T>, customRef: ForwardedRef<AgGridReact>) {
  const {
    idKey = 'id',
    className,
    title,
    radius,
    isLoading = false,
    rowData = [],
    useColumnNo = false,
    isColumnNumPinned = false,
    isSearchVisible = true,
    columnDefs,
    gridOptions,
    rowSelection = 'multiple',
    rowSelectable,
    pagination = true,
    countPerPage = 0,
    totalCount: _totalCount = 0,
    currentPage: _currentPage = 0,
    disabledExport,
    disabledUpload,
    exportType = 'public',
    exportPersonalInfoOption = false,
    exportArea,
    exportWithPassword,
    exportAudit,
    exportServerAudit,
    exportButtonLabel,
    useColumnFilter,
    buttons,
    addButtonName = '추가',
    onAddRow,
    onDeleteRows,
    onChangePage,
    onUploadData,
    onExportData,
    onSelectionChanged,
    summaryRowData,
    optionActionButton,
    gridKey,
    legacyStorageKey,
    ...rest
  } = props

  const $ref = useRef<AgGridReact>(null)
  const savedColumns = useTableColumnDefs({ columnDefs, gridKey, legacyStorageKey })
  const [checkedNum, setCheckedNum] = useCheckedNum()
  const [totalCount, setTotalCount] = useState(_totalCount)
  const [currentPage, setCurrentPage] = useState(_currentPage)
  const [searchText, setSearchText] = useState('')

  const useFilterModel = useRef(true)
  const filterModelRef = useRef<Record<string, unknown> | null>(null)

  const excelDownloadModal = useModal()
  const id = useMemo(Utils.uuid, [])
  const totalPages = useMemo(() => Math.ceil(totalCount / countPerPage), [totalCount, countPerPage])

  // Option Action Button 드롭다운 상태 관리
  const [isOptionDropdownOpen, setIsOptionDropdownOpen] = useState(false)
  const optionButtonRef = useRef<HTMLDivElement>(null)

  useClickOutside(optionButtonRef, () => setIsOptionDropdownOpen(false), isOptionDropdownOpen)

  const handleOptionClick = useCallback((onClick: () => void) => {
    onClick()
    setIsOptionDropdownOpen(false)
  }, [])

  const { rangeOnPage, isAvailablePrevPage, isAvailableNextPage } = useMemo(() => {
    const startIdx = (currentPage - 1) * countPerPage + countPerPage + 1
    const endIdx = min([(currentPage + 1) * countPerPage, totalCount])

    return {
      rangeOnPage:
        countPerPage && totalPages ? `${Utils.getCommaNum(startIdx)} ~ ${Utils.getCommaNum(endIdx)} of ${Utils.getCommaNum(totalCount)}` : '',
      isAvailablePrevPage: currentPage < 1,
      isAvailableNextPage: currentPage >= totalPages - 1,
    }
  }, [currentPage, totalCount, countPerPage])

  const columns = useMemo(() => {
    const copied = cloneDeep(savedColumns.columnDefs) || []

    if (useColumnNo) {
      copied.unshift({
        headerName: 'No',
        cellRenderer: (params: ICellRendererParams) => currentPage * countPerPage + (params.node?.rowIndex ?? 0) + 1,
        width: 80,
        pinned: isColumnNumPinned && 'left',
      })
    }

    if (rowSelectable) {
      copied.unshift({
        pinned: true,
        width: 40,
        checkboxSelection: true,
        headerCheckboxSelection: rowSelection === 'multiple',
        showDisabledCheckboxes: true,
      })
    }

    return copied.map((col) => {
      const cellClass: string[] = typeof col.cellClass === 'string' ? [col.cellClass] : Array.isArray(col.cellClass) ? col.cellClass : []

      if (useColumnFilter) {
        col.filter = 'agSetColumnFilter'
      }

      if (col.editable) {
        col.suppressKeyboardEvent = (params) => {
          const key = params.event.key
          return key === 'Backspace' || key === 'Delete'
        }

        if (typeof col.headerName === 'string') {
          col.headerName = `✎ ${col.headerName}`
          col.headerClass = TABLE_HEADER_EDITABLE
        }
      }

      col.cellClass = cellClass.join(' ')
      return col
    })
  }, [savedColumns.columnDefs, rowSelection, useColumnFilter, rowSelectable, currentPage, countPerPage])

  const onPagination = useCallback(
    (page: number) => {
      if (onChangePage) {
        useFilterModel.current = false
        onChangePage(page)
        return
      }
      setCurrentPage(page)
      $ref.current?.api.paginationGoToPage(page)
    },
    [onChangePage],
  )
  const onFilterChanged = useCallback(
    (e: FilterChangedEvent<T>) => {
      if (onChangePage) {
        // Pagination으로 인한 필터 초기화가 아닌 경우 저장
        if (useFilterModel.current) {
          filterModelRef.current = e.api.getFilterModel()
        }
        return
      }
      setTotalCount(e.api.getDisplayedRowCount())
    },
    [_currentPage, currentPage, onChangePage],
  )

  const onFilterTextBoxChanged = useCallback((v: string) => {
    setSearchText(v)
    $ref.current?.api?.setQuickFilter(v)
  }, [])

  const overlayNoRow = useMemo(() => {
    if (isLoading) return '<span class="ag-overlay-loading-center">Loading...</span>'
    return `<div style="color:#7e7e7e;z-index:0;"><div style="margin-top:12px;font-size:14px;line-height:20px;font-weight:600;">조회결과 없음</div><div style="margin-top:2px;font-size:12px;line-height:18px;color:#b3b3b3;">필터를 설정해서 조회해 주세요.</div></div>`
  }, [isLoading])

  const onGetRowId = useMemo(() => {
    if (rowSelectable) {
      return (params: GetRowIdParams<T>) => {
        const rowId = params.data?.[idKey as keyof T]

        // FIXME: rowId 유니크하게 변경 필요
        return rowId ? rowId.toString() : 'moms-row-id'
      }
    }
  }, [idKey, rowSelectable])

  const handleDeleteRow = useCallback(() => {
    const $api = $ref.current?.api
    if (!$api) return

    const selected = $api.getSelectedRows()
    if (!selected.length) return MToast.error('선택된 Row가 없습니다')

    MConfirm.show(`선택된 ${selected.length}개 Row를 정말 삭제하시겠어요?`, {
      onConfirm: () => onDeleteRows?.(selected),
    })
  }, [idKey, onDeleteRows])

  const handleSelectionChanged = useCallback(
    (params: SelectionChangedEvent) => {
      if (onSelectionChanged) onSelectionChanged(params)

      const selectedRows = params.api.getSelectedRows()
      const selectedRowsCount = selectedRows.length
      setCheckedNum(selectedRowsCount)

      params.api.redrawRows()
    },
    [onSelectionChanged],
  )

  const handleCellValueChanged = useCallback((params: CellValueChangedEvent) => {
    if (['edit', 'paste'].includes(params.source || '') && params.oldValue !== params.newValue) {
      params.node.setSelected(true)

      // 한글인 경우 엔터로 바로 stop editing 처리가 안되는 ag-grid 버그가 있어서 직접 처리.
      if (typeof params.rowIndex === 'number') {
        params.api.stopEditing()
        params.api.setFocusedCell(params.rowIndex, params.column)
      }
    }
  }, [])

  const onClickButton = useCallback(
    (onClick?: GridTableBtnClickAction<T>) => () => {
      if (!onClick || !$ref.current || !$ref.current.api) return

      const api = $ref.current.api
      api.stopEditing()
      onClick(api.getSelectedRows() || [], () => api.deselectAll())
    },
    [],
  )

  useEffect(() => {
    if ($ref.current && customRef) (customRef as MutableRefObject<AgGridReact>).current = $ref.current
  }, [])

  useEffect(() => {
    setTotalCount(_totalCount)
  }, [_totalCount])

  useEffect(() => {
    if (filterModelRef.current) {
      $ref.current?.api.setFilterModel(filterModelRef.current)
      setTimeout(() => {
        useFilterModel.current = true
      }, 0)
    }
  }, [rowData])

  useEffect(() => {
    setCurrentPage(_currentPage)
  }, [_currentPage])

  useEffect(() => {
    if (isLoading) {
      $ref.current?.api?.showLoadingOverlay()
    } else {
      // 로딩이 끝났을 때 데이터가 있으면 오버레이 숨김, 없으면 NoRows 오버레이 표시
      if (rowData && rowData.length > 0) {
        $ref.current?.api?.hideOverlay()
      } else {
        $ref.current?.api?.showNoRowsOverlay()
      }
    }
  }, [isLoading, rowData])

  return (
    <GridTableWrap $radius={radius} className={className}>
      <GridTitleWrap gap={8} align="center" justify={title ? 'space-between' : 'flex-end'}>
        {title && <Text kind="Headline_16_bold">{title}</Text>}
        <HStack gap={8} align="center">
          {isSearchVisible && <GridToolBarSearch placeholder="목록 검색" value={searchText} onChange={onFilterTextBoxChanged} />}
          {isSearchVisible && (optionActionButton || onUploadData || onExportData) && (
            <div style={{ width: '1px', height: '8px', backgroundColor: colors.gray300 }} />
          )}
          {optionActionButton && (
            <OptionButtonContainer ref={optionButtonRef}>
              <Button
                fit
                kind={optionActionButton.kind ?? 'secondary'}
                size={optionActionButton.size ?? 'sm'}
                content={optionActionButton.content}
                disabled={optionActionButton.disabled}
                onClick={optionActionButton.options ? () => setIsOptionDropdownOpen((prev) => !prev) : optionActionButton.onClick}
                {...(optionActionButton.type && { type: optionActionButton.type })}
              />
              {optionActionButton.options && isOptionDropdownOpen && (
                <OptionDropdown>
                  {optionActionButton.options.map((option, idx) => (
                    <OptionItem
                      key={`option-${id}-${idx}`}
                      onClick={() => !option.disabled && handleOptionClick(option.onClick)}
                      $disabled={option.disabled}
                    >
                      <Text kind="Body_14_regular">{option.label}</Text>
                    </OptionItem>
                  ))}
                </OptionDropdown>
              )}
            </OptionButtonContainer>
          )}
          {onExportData && (
            <Button
              fit
              kind="secondary"
              size="sm"
              content={exportButtonLabel ?? '다운로드'}
              disabled={disabledExport}
              onClick={exportType === 'private' ? excelDownloadModal.onOpenModal : () => onExportData?.()}
            />
          )}
          {onUploadData && (
            <FileInput
              accept=".xlsx, .xls, .csv"
              trigger={<Button fit kind="secondary" disabled={disabledUpload} size="sm" content="업로드" />}
              onChange={onUploadData}
            />
          )}
          {onDeleteRows && <Button fit kind="gray" size="sm" content="삭제" onClick={handleDeleteRow} />}
          {onAddRow && <Button fit kind="primary_light" size="sm" content={addButtonName} onClick={onAddRow} />}
        </HStack>
      </GridTitleWrap>
      <AgGridReactWrap className="ag-theme-balham">
        <AgGridReact<T>
          ref={$ref}
          {...agGridOptions}
          rowSelection={rowSelection}
          gridOptions={{
            ...defaultGridOptions,
            ...gridOptions,
            getRowId: onGetRowId,
          }}
          overlayNoRowsTemplate={overlayNoRow}
          defaultColDef={defaultColDef}
          rowData={rowData}
          columnDefs={columns}
          pagination={pagination}
          paginationPageSize={countPerPage}
          pinnedTopRowData={summaryRowData ? [summaryRowData] : undefined}
          onSelectionChanged={handleSelectionChanged}
          onCellValueChanged={handleCellValueChanged}
          onFilterChanged={onFilterChanged}
          {...rest}
          {...savedColumns.gridProps}
        />
      </AgGridReactWrap>

      <GridToolBarWrap gap={8} align="center" style={{ padding: '8px 16px' }}>
        {buttons?.length ? (
          <GridTableBtnList gap={12} justify="flex-end">
            <Text kind="Body_14_medium" color={colors.gray500}>
              체크한 행
              <Text kind="Body_14_medium" color={checkedNum ? colors.blue500 : colors.gray500} style={{ marginLeft: '2px' }}>
                {checkedNum}
              </Text>
              개를
            </Text>
            {buttons.map(({ onClick, ...btn }, idx) => (
              <Button {...btn} size="sm" onClick={onClickButton(onClick)} key={`grid-table-${id}-${idx}`} disabled={!checkedNum || btn.disabled} />
            ))}
          </GridTableBtnList>
        ) : null}

        {pagination && (
          <GridToolPagination gap={32} $loading={isLoading} align="center" justify="flex-end">
            <span>{rangeOnPage}</span>
            <HStack gap={4} align="center">
              <RemixIcon name="ri-skip-left-line" disabled={isAvailablePrevPage} onClick={() => onPagination(0)} />
              <RemixIcon name="ri-arrow-left-s-line" disabled={isAvailablePrevPage} onClick={() => onPagination(currentPage - 1)} />
              <span>
                Page {currentPage + 1} of {totalPages || totalPages + 1}
              </span>
              <RemixIcon name="ri-arrow-right-s-line" disabled={isAvailableNextPage} onClick={() => onPagination(currentPage + 1)} />
              <RemixIcon name="ri-skip-right-line" disabled={isAvailableNextPage} onClick={() => onPagination(totalPages - 1)} />
            </HStack>
          </GridToolPagination>
        )}
      </GridToolBarWrap>
      {excelDownloadModal.open && (
        <ExcelDownloadModal
          open
          personalInfoOption={exportPersonalInfoOption}
          withPassword={exportWithPassword}
          area={exportArea}
          audit={exportAudit}
          serverAudit={exportServerAudit}
          onConfirm={onExportData}
          onClose={excelDownloadModal.onCloseModal}
        />
      )}
    </GridTableWrap>
  )
}

const OptionButtonContainer = styled.div`
  position: relative;
`

const OptionDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 100%;
  background-color: ${colors.white};
  border: 1px solid ${colorSystem.border.element};
  border-radius: ${borderRadiusSystem.sm}px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow: hidden;
`

const OptionItem = styled.div<{ $disabled?: boolean }>`
  padding: 8px 12px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  background-color: ${colors.white};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ $disabled }) => ($disabled ? colors.white : colorSystem.bg.common.secondary_hover)};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${colorSystem.border.element};
  }
`

const GridTable = forwardRef(GridTableInner) as <T>(
  props: GridTableProps<T> & { ref?: ForwardedRef<AgGridReact> },
) => ReturnType<typeof GridTableInner>

export default GridTable
